import { Box, Container, Text } from 'atomic'
import SocialMedia from 'components/builder_components/social_media/SocialMedia'
import AppStore from 'components/icons/AppStore'
import GooglePlay from 'components/icons/GooglePlay'
import OUTextLogo from 'components/icons/OU_TextLogo'
import { Link } from 'components/link/Link'
import { FooterData } from 'context/LinkContext'
import { useRouter } from 'next/router'
import React, { FC } from 'react'
import * as css from './Footer.css'
import { useSafeTranslation } from 'hooks/useSafeTranslation'
import Image from 'next/image'
import BCorpImage from 'public/images/bCorp_Logo.png'
import GPTWBadge from 'public/images/Once_Upon_Publishing_AB_SE_English_2025_Certification_Badge.webp'
import useAppLinks from 'hooks/useAppLinks'
import { trackOutboundLink } from 'lib/gtag'
import { ouTheme } from 'atomic/styles/theme.css'
import { LanguageRegionPickerButton } from 'components/languageregionpicker/LanguageRegionPickerModal'
import PaymentMethods from 'components/payment_methods/PaymentMethods'

type FooterProps = {
  footerData?: FooterData
}

const defaultLocale = 'en-gb'

const Footer: FC<FooterProps> = ({ footerData }) => {
  const router = useRouter()
  const { locale } = router
  const localeIsNotDefault = locale !== defaultLocale
  const { t } = useSafeTranslation()
  const { appStoreUrl } = useAppLinks()
  const { googlePlayUrl } = useAppLinks()

  return (
    <Box backgroundColor={'kantarell2'} paddingY={'xxl'}>
      <Container
        display={'flex'}
        flexDirection={{ mobile: 'column', desktop: 'row' }}
        alignItems={'flex-start'}
        justifyContent={'flex-start'}
        gap={{ mobile: 'xl', desktop: 'l' }}
      >
        <LanguageRegionPickerButton />

        <Box display={{ mobile: 'none', desktop: 'flex' }} flexDirection={'column'} gap={'s'} style={{ width: '70%' }}>
          <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
            {t('website_footer_option_3')}
          </Text>
          <Box display={'flex'} flexDirection={'column'} gap={'xxxs'}>
            {footerData?.data?.informationNavLinks?.map((link, index) => (
              <Link key={index} href={link.externalLink ? link.url : localeIsNotDefault ? `/${locale}${link.url}` : link.url}>
                <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} className={css.links}>
                  {link.label}
                </Text>
              </Link>
            ))}
          </Box>
        </Box>
        <Box display={{ mobile: 'none', desktop: 'flex' }} flexDirection={'column'} gap={'s'} style={{ width: '70%' }}>
          <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
            {t('website_footer_option_4')}
          </Text>
          <Box display={'flex'} flexDirection={'column'} gap={'xxxs'}>
            {footerData?.data?.aboutNavLinks?.map((link, index) => (
              <Link key={index} href={link.externalLink ? link.url : localeIsNotDefault ? `/${locale}${link.url}` : link.url}>
                <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} className={css.links}>
                  {link.label}
                </Text>
              </Link>
            ))}
          </Box>
        </Box>
        {/* Need to make a separate Box for mobile to fit the design */}
        <Box display={{ mobile: 'flex', desktop: 'none' }} gap={{ mobile: 'l', desktop: 'l' }} style={{ width: '100%' }}>
          <Box display={'flex'} flexDirection={'column'} gap={'s'} style={{ width: 'fit-content' }}>
            <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
              {t('website_footer_option_3')}
            </Text>
            <Box display={'flex'} flexDirection={'column'} gap={'xxxs'}>
              {footerData?.data?.informationNavLinks?.map((link, index) => (
                <Link
                  key={index}
                  href={link.externalLink ? link.url : localeIsNotDefault ? `/${locale}${link.url}` : link.url}
                  style={{ minHeight: 24 }} // need this for accessibility reasons and enough clickable area
                >
                  <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} className={css.links}>
                    {link.label}
                  </Text>
                </Link>
              ))}
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={'s'} style={{ width: 'fit-content' }}>
            <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
              {t('website_footer_option_4')}
            </Text>
            <Box display={'flex'} flexDirection={'column'} gap={'xxxs'}>
              {footerData?.data?.aboutNavLinks?.map((link, index) => (
                <Link
                  key={index}
                  href={link.externalLink ? link.url : localeIsNotDefault ? `/${locale}${link.url}` : link.url}
                  style={{ minHeight: 24 }} // need this for accessibility reasons and enough clickable area
                >
                  <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} className={css.links}>
                    {link.label}
                  </Text>
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={'xl'} style={{ width: '90%' }} marginBottom={{ mobile: 'xl', desktop: 'none' }}>
          <Box display={'flex'} flexDirection={'column'} gap={'s'} style={{ width: '100%' }}>
            <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
              {t('website_footer_option_2')}
            </Text>
            <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} flexWrap={'wrap'} gap={'xxs'}>
              <PaymentMethods poweredByStripeColor={'black'} size={'s'} />
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={'s'} style={{ width: '100%' }}>
            <Text fontSize={'s'} fontWeight={'extrabold'} marginBottom={'none'}>
              {t('website_footer_option_1')}
            </Text>
            <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} flexWrap={'wrap'} gap={'xxs'}>
              <Link onClick={() => trackOutboundLink(appStoreUrl)} href={appStoreUrl} target="_blank" aria-label="Link to Appstore">
                <AppStore locale={locale} />
              </Link>
              <Link onClick={() => trackOutboundLink(googlePlayUrl)} href={googlePlayUrl} target="_blank" aria-label="Link to Google Play">
                <GooglePlay locale={locale} />
              </Link>
            </Box>
          </Box>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={{ mobile: 'flex-start', desktop: 'flex-end' }}
          gap={{ mobile: 'l', desktop: 's' }}
          style={{ width: '100%' }}
        >
          <OUTextLogo width={188} />
          <Box display={'flex'} flexDirection={'row'} gap={'xxs'} alignItems={'center'}>
            <Image
              src={GPTWBadge}
              alt="Great Place To Work certified badge"
              width={48}
              height={70}
              sizes={`(max-width: ${ouTheme.breakpoints.tablet}) 100vw, 50vw`}
              fetchPriority="low"
              loading="lazy"
            />
            <Image
              src={BCorpImage}
              alt="B Corp certified badge"
              width={48}
              height={70}
              sizes={`(max-width: ${ouTheme.breakpoints.tablet}) 100vw, 50vw`}
              fetchPriority="low"
              loading="lazy"
            />
          </Box>
          <Box display={'flex'} flexDirection={'column'} alignItems={{ mobile: 'flex-start', desktop: 'flex-end' }} gap={'m'}>
            <SocialMedia socialLink={footerData?.data?.socialMediaLinks} iconSize={'l'} />
            <Box display={'flex'} flexDirection={'column'} alignItems={{ mobile: 'flex-start', desktop: 'flex-end' }} gap={'none'}>
              <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} textAlign={{ mobile: 'left', desktop: 'right' }}>
                {footerData?.data?.companyInformation?.nameOrgNum}
              </Text>
              <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} textAlign={{ mobile: 'left', desktop: 'right' }}>
                {footerData?.data?.companyInformation?.address}
              </Text>
              <Text fontSize={'s'} fontWeight={'light'} marginBottom={'none'} textAlign={{ mobile: 'left', desktop: 'right' }}>
                {footerData?.data?.companyInformation?.copyrightEmail}
              </Text>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
